import {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useMemo,
} from 'react';
import { Popup } from '@/modules/Popup';
import { InnerButton } from '@/modules/InnerButton';
import { Dropdown } from '@/modules/Dropdown';
import { Icon } from '@/modules/Icon';
import { PopupRef } from '@/shared/types/popup';
import { FilterCard } from '../molecules/FilterCard';
import { LogicOperatorSelector } from '../molecules/LogicOperatorSelector';
import {
  AdvancedFilterConfig,
  FilterColumnOption,
  FILTER_OPERATORS,
  LogicOperator,
} from '../../types/filter';
import { ColumnConfig, RowConfig } from '@/shared/types/table';
import { Title } from '@/modules/Title';
import { Separator } from '@/modules/Separator';
import {
  faCheck,
  faFilter,
  faFilterList,
  faPlus,
  faText,
  faHashtag,
  faListUl,
  faCalendar,
  faBroomWide,
} from '@fortawesome/pro-regular-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
export interface FilterPopupRef {
  open: () => void;
  close: () => void;
}

interface FilterPopupProps<T = unknown> {
  columns: ColumnConfig<T>[];
  rows: RowConfig<T>[]; // Datos de la tabla para extraer valores únicos
  filters: AdvancedFilterConfig[];
  onFiltersApply: (filters: AdvancedFilterConfig[]) => void;
  className?: string;
}

export const FilterPopup = forwardRef<FilterPopupRef, FilterPopupProps<unknown>>(
  ({ columns, rows, filters, onFiltersApply, className }, ref) => {
    const popupRef = useRef<PopupRef>(null);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [tempFilters, setTempFilters] = useState<AdvancedFilterConfig[]>([]);
    const [selectedColumn, setSelectedColumn] = useState<string>('');

    // Función para extraer valores únicos de una columna
    const extractUniqueValues = useCallback(
      (column: ColumnConfig<unknown>): Array<{ value: string | number; label: string }> => {
        const valuesSet = new Set<string | number>();

        rows.forEach((row) => {
          let value: unknown;

          // Obtener el valor usando el accessor
          if (typeof column.accessor === 'function') {
            value = column.accessor(row);
          } else if (column.accessor) {
            const rowRecord = row as Record<string, unknown>;
            value = rowRecord[column.accessor as string];
          } else {
            const rowRecord = row as Record<string, unknown>;
            value = rowRecord[column.id];
          }

          // Si el valor es un array (tags, avatars), agregar cada elemento
          if (Array.isArray(value)) {
            value.forEach((item: unknown) => {
              // Si el item es un objeto, intentar obtener una propiedad común
              if (typeof item === 'object' && item !== null) {
                const itemObj = item as Record<string, unknown>;
                const val =
                  (itemObj.name as string | number) ||
                  (itemObj.label as string | number) ||
                  (itemObj.value as string | number) ||
                  (itemObj.id as string | number) ||
                  JSON.stringify(item);
                valuesSet.add(String(val));
              } else {
                valuesSet.add(String(item));
              }
            });
          } else if (value !== null && value !== undefined && value !== '') {
            // Si es un objeto, intentar obtener una propiedad común
            if (typeof value === 'object') {
              const valueObj = value as Record<string, unknown>;
              const val =
                (valueObj.name as string | number) ||
                (valueObj.label as string | number) ||
                (valueObj.value as string | number) ||
                (valueObj.id as string | number) ||
                JSON.stringify(value);
              valuesSet.add(String(val));
            } else {
              valuesSet.add(String(value));
            }
          }
        });

        // Convertir el Set a array de opciones
        const options = Array.from(valuesSet).map((val) => ({
          value: val,
          label: String(val),
        }));

        // Ordenar alfabéticamente por label
        return options.sort((a, b) => a.label.localeCompare(b.label));
      },
      [rows]
    );

    // Sincronizar filtros temporales cuando se abre el popup
    useEffect(() => {
      if (!isPopupOpen) {
        // Asegurar que todos los filtros tengan un logicOperator por defecto
        const filtersWithLogic = filters.map((filter) => ({
          ...filter,
          logicOperator: filter.logicOperator || ('and' as LogicOperator),
        }));
        setTempFilters(filtersWithLogic);
      }
    }, [filters, isPopupOpen]);
    // text - text / numeric - hashtag / list - list-ul / date - calendar
    // Convertir columnas a opciones para el dropdown
    const columnOptions: FilterColumnOption[] = useMemo(
      () =>
        columns
          .filter((col) => {
            // Filtrar columnas que no son filtrables
            if (col.filterable === false || col.visible === false) return false;
            // Excluir tipos que no tienen filtro
            const nonFilterableTypes = ['actions', 'textfield', 'skeleton'];
            if (col.type && nonFilterableTypes.includes(col.type)) return false;
            return true;
          })
          .map((col) => {
            // Mapear el tipo de columna a tipo de dato para filtros
            let dataType: 'text' | 'number' | 'date' | 'list';
            let options: Array<{ value: string | number; label: string }> | undefined = undefined;
            let icon: IconDefinition;

            // Según especificación del usuario:
            // Text: default, primary, status, link, dropdown, button - icon: faText
            // Numeric: progressbar, stars, number, numeric - icon: faHashtag
            // List: avatars, tags, checkbox - icon: faListUl
            // Date: date - icon: faCalendar

            switch (col.type) {
              case 'number':
              case 'numeric':
              case 'progressbar':
              case 'stars':
                dataType = 'number';
                icon = faHashtag;
                break;
              case 'date':
                dataType = 'date';
                icon = faCalendar;
                break;
              case 'avatars':
              case 'tags':
              case 'checkbox':
                dataType = 'list';
                icon = faListUl;
                // Extraer valores únicos de los datos si no se proporcionaron opciones predefinidas
                options = col.filterOptions || extractUniqueValues(col);
                break;
              // Todos los demás son text por defecto
              default:
                dataType = 'text';
                icon = faText;
            }

            // `filterOptions` en columnas de texto (p. ej. default/primary/status) fuerza filtro tipo lista
            if (dataType === 'text' && col.filterOptions && col.filterOptions.length > 0) {
              dataType = 'list';
              icon = faListUl;
              options = col.filterOptions;
            }

            return {
              value: col.id,
              label: typeof col.header === 'string' ? col.header : col.id,
              dataType,
              options,
              icon,
            };
          }),
      [columns, extractUniqueValues]
    );

    // Handlers
    const handleCancel = useCallback(() => {
      setTempFilters(filters);
      setSelectedColumn('');
      setIsPopupOpen(false);
      popupRef.current?.close();
    }, [filters]);

    const handlePopupOpen = useCallback(() => {
      setIsPopupOpen(true);
      // Asegurar que todos los filtros tengan un logicOperator por defecto
      const filtersWithLogic = filters.map((filter) => ({
        ...filter,
        logicOperator: filter.logicOperator || ('and' as LogicOperator),
      }));
      setTempFilters(filtersWithLogic);
      setSelectedColumn('');
    }, [filters]);

    const handleSave = useCallback(() => {
      // Filtrar solo los filtros que tienen operador y valor (si es necesario)
      const validFilters = tempFilters.filter((filter) => {
        const operatorConfig = FILTER_OPERATORS.find((op) => op.value === filter.operator);
        if (!operatorConfig) return false;
        if (!operatorConfig.requiresValue) return true;

        // Para operador "in" (Es uno de), verificar que el array tenga elementos
        if (filter.operator === 'in') {
          return Array.isArray(filter.value) && filter.value.length > 0;
        }

        // Para operadores con segundo valor (between)
        if (operatorConfig.requiresSecondValue) {
          return (
            filter.value &&
            filter.value.toString().trim() !== '' &&
            filter.value2 &&
            filter.value2.toString().trim() !== ''
          );
        }

        // Para operadores normales
        return filter.value && filter.value.toString().trim() !== '';
      });

      onFiltersApply(validFilters);
      setIsPopupOpen(false);
      popupRef.current?.close();
    }, [tempFilters, onFiltersApply]);

    const handleClearAll = useCallback(() => {
      setTempFilters([]);
    }, []);

    // Agregar nueva tarjeta de filtro cuando se hace clic en (+)
    const handleAddFilterCard = useCallback(() => {
      if (!selectedColumn) return;

      const columnData = columnOptions.find((col) => col.value === selectedColumn);
      if (!columnData) return;

      const newFilter: AdvancedFilterConfig = {
        id: `filter-${Date.now()}-${Math.random()}`,
        columnId: selectedColumn,
        columnLabel: columnData.label,
        operator: 'equals', // Operador por defecto
        value: '',
        value2: undefined,
        dataType: columnData.dataType,
        logicOperator: 'and', // Compuerta lógica por defecto
      };

      setTempFilters((prev) => [...prev, newFilter]);
      setSelectedColumn(''); // Resetear selector
    }, [selectedColumn, columnOptions]);

    // Actualizar un filtro existente
    const handleUpdateFilter = useCallback(
      (filterId: string, updates: Partial<AdvancedFilterConfig>) => {
        setTempFilters((prev) =>
          prev.map((filter) => (filter.id === filterId ? { ...filter, ...updates } : filter))
        );
      },
      []
    );

    // Eliminar un filtro
    const handleRemoveFilter = useCallback((filterId: string) => {
      setTempFilters((prev) => prev.filter((f) => f.id !== filterId));
    }, []);

    // Actualizar compuerta lógica de un filtro
    const handleUpdateLogicOperator = useCallback(
      (filterId: string, logicOperator: LogicOperator) => {
        setTempFilters((prev) =>
          prev.map((filter) => (filter.id === filterId ? { ...filter, logicOperator } : filter))
        );
      },
      []
    );

    // Verificar si hay cambios entre filtros aplicados y filtros temporales
    const hasChanges = useMemo(() => {
      // Si las longitudes son diferentes, hay cambios
      if (tempFilters.length !== filters.length) return true;

      // Si ambos están vacíos, no hay cambios
      if (tempFilters.length === 0 && filters.length === 0) return false;

      // Crear función para serializar un filtro (ignorando el id para comparar)
      const serializeFilter = (filter: AdvancedFilterConfig) =>
        `${filter.columnId}|${filter.operator}|${filter.value || ''}|${filter.value2 || ''}|${filter.logicOperator || ''}`;

      // Crear sets de filtros serializados para comparar independientemente del orden
      const appliedSet = new Set(filters.map(serializeFilter));
      const tempSet = new Set(tempFilters.map(serializeFilter));

      // Si los tamaños son diferentes, hay cambios
      if (appliedSet.size !== tempSet.size) return true;

      // Verificar si todos los filtros temporales están en los aplicados
      for (const tempFilter of tempSet) {
        if (!appliedSet.has(tempFilter)) return true;
      }

      return false;
    }, [tempFilters, filters]);

    // Exponer métodos para abrir/cerrar el popup desde afuera
    useImperativeHandle(
      ref,
      () => ({
        open: () => {
          setIsPopupOpen(true);
          // Asegurar que todos los filtros tengan un logicOperator por defecto
          const filtersWithLogic = filters.map((filter) => ({
            ...filter,
            logicOperator: filter.logicOperator || ('and' as LogicOperator),
          }));
          setTempFilters(filtersWithLogic);
          popupRef.current?.open?.();
        },
        close: handleCancel,
      }),
      [filters, handleCancel]
    );

    return (
      <Popup
        ref={popupRef}
        className="ui-flex ui-min-w-[500px] ui-flex-col ui-gap-0"
        theme="solid"
        color="secondary"
        position="bottom-left"
        icon={faFilterList}
        size="xs"
        allowOverflow
        closeOnClick={false}
        // closeOnClickOutside={false}
        onOpen={handlePopupOpen}
        tooltipProps={{ label: 'Filtros avanzados', position: 'top-left' }}
        absolute
        pill="sm"
      >
        {/* Header */}
        <div className="ui-flex ui-items-center ui-justify-between ui-p-16">
          <Title title="Filtros Avanzados" icon={faFilter} />
        </div>
        <Separator />
        {/* Content: sin overflow-y en el cuerpo para no recortar Dropdown con popover.absolute */}
        <div className="ui-flex ui-flex-col ui-gap-0 ui-overflow-visible">
          {/* Dropdown para agregar filtro */}
          <div className="ui-bg-surface-secondary-default ui-p-16">
            <div className="ui-flex ui-items-end ui-gap-8">
              <div className="ui-flex ui-w-full ui-flex-col ui-gap-4">
                <Dropdown
                  field={{ label: 'Agregar filtro', placeholder: 'Columnas', size: 'xs' }}
                  list={{ listVariant: 'filters', filtersTitle: 'Agregar filtro' }}
                  items={{
                    options: columnOptions.map((col) => ({
                      value: col.value,
                      label: col.label,
                      icon: col.icon,
                    })),
                    valueName: 'label',
                    idName: 'value',
                    defaultOptionSelected: false,
                  }}
                  values={{ value: selectedColumn }}
                  events={{ onValueChange: (val) => setSelectedColumn(val as string) }}
                  popover={{ absolute: true }}
                />
              </div>
              <InnerButton
                theme="solid"
                color="secondary"
                size="xs"
                iconProps={{ icon: faPlus }}
                onClick={handleAddFilterCard}
                disabled={!selectedColumn}
                tooltipProps={{ label: 'Añadir', position: 'top-left' }}
                pill="pill"
              />
            </div>
          </div>
          <Separator />
          {/* Tarjetas de filtros: overflow visible para menús absolute (sin portal) */}
          <div className="ui-overflow-visible ui-px-16 ui-py-32">
            {tempFilters.length === 0 ? (
              <div className="ui-flex ui-flex-col ui-items-center ui-justify-center ui-gap-y-8">
                <Icon name={faFilter} size="xl" color="blue" background padding="lg" />

                <p className="ui-leading-tracking-body-md ui-text-body-md-regular ui-font-body-md-semibold ui-leading-body-md-bold ui-tracking-body-md">
                  No hay Filtros configurados
                </p>
                <p className="ui-leading-tracking-body-xs ui-text-center ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-body-xs ui-text-tertiary">
                  Agrega un filtro seleccionando una columna
                </p>
              </div>
            ) : (
              <div className="ui-flex ui-flex-col ui-gap-0">
                {tempFilters.map((filter, index) => {
                  const columnData = columnOptions.find((col) => col.value === filter.columnId);
                  if (!columnData) return null;

                  const isLastFilter = index === tempFilters.length - 1;

                  return (
                    <div key={filter.id}>
                      <FilterCard
                        filter={filter}
                        columnData={columnData}
                        onUpdate={handleUpdateFilter}
                        onRemove={handleRemoveFilter}
                      />

                      {/* Mostrar selector de compuerta lógica solo si no es el último filtro */}
                      {!isLastFilter && (
                        <LogicOperatorSelector
                          value={filter.logicOperator || 'and'}
                          onChange={(logicOp) => handleUpdateLogicOperator(filter.id, logicOp)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <Separator />

        {/* Footer */}
        <div className="ui-flex ui-items-center ui-justify-between ui-p-16">
          {/* Botón Limpiar todo (izquierda) */}
          {tempFilters.length > 0 && (
            <InnerButton
              theme="solid"
              color="secondary"
              size="xs"
              iconProps={{ icon: faBroomWide }}
              onClick={handleClearAll}
              pill="pill"
              tooltipProps={{ label: 'Limpiar filtros', position: 'top-left' }}
            />
          )}

          {/* Botones Cancelar y Aplicar (derecha) */}
          <div className="ui-ml-auto ui-flex ui-gap-3">
            <InnerButton theme="solid" color="secondary" size="xs" pill="md" onClick={handleCancel}>
              Cancelar
            </InnerButton>
            <InnerButton
              theme="solid"
              color="primary"
              size="xs"
              pill="md"
              iconProps={{ icon: faCheck }}
              onClick={handleSave}
              disabled={!hasChanges}
            >
              Aplicar Filtros
            </InnerButton>
          </div>
        </div>
      </Popup>
    );
  }
);

FilterPopup.displayName = 'FilterPopup';
